import { verificationsApi } from '../../../data/verificationsApi';
import type {
  CheckEligibilityResponse,
  PatientResponsibilityCalcRequest,
  PatientResponsibilityCalcResponse,
  ReimbursementCalcResult,
  ReimbursementProcedureLine,
} from '../../../data';
import type {
  CreatePreQuoteRequest,
  PreQuoteListItemView,
  PreQuoteSecondaryCoverageCheckResult,
  PreQuoteView,
  UpdatePreQuoteRequest,
} from './preQuoteViews';

/**
 * Pre-Quote feature endpoints (spec 057), injected into the single verifications base api. Owns
 * the `verification/pre-quotes/*` CRUD routes (contract §2) plus the FR-012 tooling seams (contract
 * §4): eligibility check, reimbursement calculate, secondary-coverage check, and patient-responsibility
 * calculate each delegate to the SAME shared cores the verification path uses and return the SAME
 * response shapes, reused here by path import (not the `data` barrel) per house style.
 * `calculatePreQuotePatientResponsibility` targets the guarded pre-quote route (FR-014) — it loads
 * the aggregate and enforces `EnsureToolingAllowed` before delegating to the identical calculator
 * core the shared `verification/patient-responsibility/calculate` route uses, so there is still
 * exactly one calculation implementation (R1c); only the guard is pre-quote-specific.
 *
 * Split into three chained `injectEndpoints` calls (CRUD / tooling / lifecycle) purely to keep each
 * builder small — RTK Query merges all three into the one `verificationsApi` cache/tag space and
 * the one `preQuoteEndpoints` export below, identical to a single call.
 */
const preQuoteCrudEndpoints = verificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreQuotes: builder.query<PreQuoteListItemView[], { company: string; center: string }>({
      query: ({ company, center }) => ({
        url: '/verification/pre-quotes',
        method: 'GET',
        params: { company, center },
      }),
      providesTags: (result) => [
        ...(result ?? []).map((item) => ({ type: 'PreQuote' as const, id: item.id })),
        'PreQuoteList',
      ],
    }),

    getPreQuoteById: builder.query<PreQuoteView, { id: string }>({
      query: ({ id }) => ({ url: `/verification/pre-quotes/${id}`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }],
    }),

    createPreQuote: builder.mutation<PreQuoteView, CreatePreQuoteRequest>({
      query: (body) => ({ url: '/verification/pre-quotes', method: 'POST', body }),
      invalidatesTags: ['PreQuoteList'],
    }),

    updatePreQuote: builder.mutation<PreQuoteView, { id: string; body: UpdatePreQuoteRequest }>({
      query: ({ id, body }) => ({ url: `/verification/pre-quotes/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }, 'PreQuoteList'],
    }),

    addPreQuoteNote: builder.mutation<PreQuoteView, { id: string; text: string }>({
      query: ({ id, text }) => ({
        url: `/verification/pre-quotes/${id}/notes`,
        method: 'POST',
        body: { text },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }],
    }),
  }),
  overrideExisting: false,
});

// FR-012 §4 tooling seams — read-only except `checkPreQuoteSecondaryCoverage`, which is the only
// tool route that persists server-side (see its own comment below).
const preQuoteToolingEndpoints = preQuoteCrudEndpoints.injectEndpoints({
  endpoints: (builder) => ({
    // Read-only (contract §4) — no persistence, no tag invalidation; the client applies the result
    // via `populateBenefits` and the existing PUT saves it (T031/T032).
    checkPreQuoteEligibility: builder.mutation<CheckEligibilityResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/verification/pre-quotes/${id}/eligibility/check`,
        method: 'POST',
        // Stedi real-time checks can take up to ~120s (mirrors `checkEligibility`).
        timeout: 125_000,
      }),
    }),

    // Read-only (contract §4) — working procedure rows, verification-endpoint parity.
    calculatePreQuoteReimbursement: builder.mutation<
      ReimbursementCalcResult,
      { id: string; procedures: ReimbursementProcedureLine[] }
    >({
      query: ({ id, procedures }) => ({
        url: `/verification/pre-quotes/${id}/reimbursement/calculate`,
        method: 'POST',
        body: { procedures },
      }),
    }),

    // Guarded pre-quote route (FR-014) — loads the aggregate and enforces `EnsureToolingAllowed`
    // before delegating to the SAME calculator core the shared verification route uses; same
    // request/response shapes as the estimate feature's `calculatePatientResponsibility`.
    calculatePreQuotePatientResponsibility: builder.mutation<
      PatientResponsibilityCalcResponse,
      { id: string } & PatientResponsibilityCalcRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/verification/pre-quotes/${id}/patient-responsibility/calculate`,
        method: 'POST',
        body,
      }),
    }),

    // Unlike the other tool routes this ONE persists (RecordSecondaryCoverage stamps
    // status/checkedAt) — the client still applies the result locally rather than invalidating the
    // `PreQuote` tag, so a refetch never clobbers other unsaved working-copy edits (T077).
    checkPreQuoteSecondaryCoverage: builder.mutation<
      PreQuoteSecondaryCoverageCheckResult,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/verification/pre-quotes/${id}/secondary-coverage/check`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

// Lifecycle transitions (quote / convert / expire / reopen) — each terminal or near-terminal,
// contract §3.
export const preQuoteEndpoints = preQuoteToolingEndpoints.injectEndpoints({
  endpoints: (builder) => ({
    // GFE-produced lifecycle transition (T037, US3 AC1/AC4). The caller reserves a blank window
    // SYNCHRONOUSLY on click, before this call, so the browser's popup blocker sees an unbroken user
    // gesture; if no window can be reserved this route is never called and no snapshot is created.
    // On success this route re-validates completeness server-side (422 { missing: [...] } when
    // blocked) and appends the ONE immutable quote snapshot (FR-025, SC-010 — never fabricated
    // client-side); the document is built client-side from THIS RESPONSE (never the pre-call working
    // copy) and written into the reserved window — never uploaded. Any failure closes the reserved
    // window instead of writing to it. Invalidates the `PreQuote` tag so the detail view's existing
    // query/slice sync refreshes the new status/snapshot.
    quotePreQuote: builder.mutation<PreQuoteView, { id: string }>({
      query: ({ id }) => ({ url: `/verification/pre-quotes/${id}/quote`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }, 'PreQuoteList'],
    }),

    // Link-only lifecycle transition (T046, US5, SC-006) — the body carries ONLY the staff-chosen
    // verification id, never any pre-quote data, so zero values are copied in either direction.
    // Invalidates the `PreQuote` tag so the detail view refreshes into the read-only Converted state.
    convertPreQuote: builder.mutation<PreQuoteView, { id: string; verificationId: string }>({
      query: ({ id, verificationId }) => ({
        url: `/verification/pre-quotes/${id}/convert`,
        method: 'POST',
        body: { verificationId },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }, 'PreQuoteList'],
    }),

    // Manual expiry (T050, contract §3) — terminal, stored, and NOT reversible by editing dates
    // (only `/reopen` undoes it). 409 on an already-terminal record. No dedicated slice reducer:
    // the `PreQuote` tag invalidation refetches `getPreQuoteById`, whose existing matcher
    // re-hydrates the working copy from the response.
    expirePreQuote: builder.mutation<PreQuoteView, { id: string }>({
      query: ({ id }) => ({ url: `/verification/pre-quotes/${id}/expire`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }, 'PreQuoteList'],
    }),

    // Reopen (T082, contract §3) — undoes ONLY a manual expiry; 409 on Draft/Quoted (nothing to
    // undo) or Converted (permanently terminal). Same tag-invalidation re-hydration as above.
    reopenPreQuote: builder.mutation<PreQuoteView, { id: string }>({
      query: ({ id }) => ({ url: `/verification/pre-quotes/${id}/reopen`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PreQuote', id }, 'PreQuoteList'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPreQuotesQuery,
  useGetPreQuoteByIdQuery,
  useCreatePreQuoteMutation,
  useUpdatePreQuoteMutation,
  useAddPreQuoteNoteMutation,
  useCheckPreQuoteEligibilityMutation,
  useCalculatePreQuoteReimbursementMutation,
  useCalculatePreQuotePatientResponsibilityMutation,
  useCheckPreQuoteSecondaryCoverageMutation,
  useQuotePreQuoteMutation,
  useConvertPreQuoteMutation,
  useExpirePreQuoteMutation,
  useReopenPreQuoteMutation,
} = preQuoteEndpoints;
